// Hosted-forms prose editor — the pure content model + validation. No React,
// no Supabase, so the editor, the (future) supplier renderer, and tests all
// agree on the schema and on what blocks publish.
//
// A form document's content is an ordered list of typed blocks:
//   heading    — a section heading (plain text)
//   paragraph  — body text as an ordered list of inline tokens: text runs and
//                inline FIELD chips (identity-only prefill)
//   clause     — a block the supplier must tick: a ≤30-word summary + the full
//                wording. Always required-to-tick.
//   signature  — appears once, at the very end: an authority statement the
//                supplier signs under.
//
// Prefill is IDENTITY ONLY, never substance: a field can only bind to an
// account/identity source (company name/number, VAT, registered address,
// today's date). There is deliberately no way to bind a field to a free-text
// or choice value the supplier is attesting to — the model structurally
// prevents it, which is why "prefill bound to a substantive field" can never
// occur here.

// ---------------------------------------------------------------------------
// Identity prefill sources — the ONLY things a field may bind to.
// ---------------------------------------------------------------------------
export const FIELD_SOURCES = [
  { key: 'company_name',       label: 'Company name',       example: 'Acme Events Ltd' },
  { key: 'company_number',     label: 'Company number',     example: '01234567' },
  { key: 'vat_number',         label: 'VAT number',         example: 'GB123456789' },
  { key: 'registered_address', label: 'Registered address', example: '1 High St, London' },
  { key: 'today',              label: "Today's date",       example: '6 Aug 2026' },
];

export const fieldLabel = (source) =>
  FIELD_SOURCES.find(s => s.key === source)?.label || source || 'Field';

// ---------------------------------------------------------------------------
// "They fill in" fields — inline inputs the SUPPLIER completes (never
// prefilled). First name and surname are DELIBERATELY separate (a signature
// block, a salutation and a certificate name need them apart, and splitting
// afterwards is unreliable). `control` picks the supplier-side input.
// ---------------------------------------------------------------------------
export const FILL_FIELDS = [
  { key: 'first_name', label: 'First name', control: 'text' },
  { key: 'surname',    label: 'Surname',    control: 'text' },
  { key: 'their_role', label: 'Their role', control: 'text' },
  { key: 'email',      label: 'Email',      control: 'email' },
  { key: 'phone',      label: 'Phone',      control: 'tel' },
  { key: 'fill_date',  label: 'Date',       control: 'date' },
  { key: 'yes_no',     label: 'Yes / no',   control: 'yesno' },
  { key: 'short_text', label: 'Short text', control: 'text' },
  { key: 'long_text',  label: 'Long text',  control: 'textarea' },
  { key: 'number',     label: 'Number',     control: 'number' },
  // Rob, 2026-08-14: "need to be able to provide areas within form building for
  // suppliers to upload attachments in response to questions." An answer is
  // often a document — the policy itself, an insurance schedule, a photo of a
  // rig — and until now the only way to give one was to paste a link, which
  // means the evidence lives somewhere Fusion doesn't control and can vanish.
  //
  // Deliberately an INLINE field like the rest, so it sits in the sentence that
  // asks for it ("Do you have a policy? [Yes/no] If yes, attach it: [Attach]")
  // rather than becoming a separate section the author has to remember to add
  // and the supplier has to connect back to the question.
  { key: 'attachment', label: 'Attachment', control: 'file' },
];
export const fillFieldLabel = (ftype) => FILL_FIELDS.find(f => f.key === ftype)?.label || 'Field';
export const fillFieldControl = (ftype) => FILL_FIELDS.find(f => f.key === ftype)?.control || 'text';
export const isFillType = (ftype) => FILL_FIELDS.some(f => f.key === ftype);

// The supplier's answer to an input token (keyed by the token's id, since a
// form can have many inputs of the same type). Falls back to the label as a
// placeholder in the stored copy if somehow unanswered.
export const inputValueOf = (token, fields) => {
  const v = fields && fields[token.id];
  return v != null && String(v).trim() ? String(v) : `[${token.label || fillFieldLabel(token.ftype)}]`;
};

export const CLAUSE_SUMMARY_MAX_WORDS = 30;

// Stable-ish id without Date.now()/Math.random() dependence at module scope —
// callers pass an index/seed. Good enough for React keys + block refs.
let _seq = 0;
export const newBlockId = () => `b${(_seq++).toString(36)}_${(_seq * 2654435761 % 1e9).toString(36)}`;

// A supplier-fills inline token. Carries its own id (keys the answer), a type,
// an editable label and an optional flag.
export function newInputToken(ftype) {
  return { t: 'input', id: newBlockId(), ftype: isFillType(ftype) ? ftype : 'short_text', label: fillFieldLabel(ftype), optional: false };
}

// ---------------------------------------------------------------------------
// Presentation TOKENS, not free values (long-documents brief §1).
//
// Rob settled the contradiction between the two briefs on 2026-08-13: authors
// DO get size, weight, underline and indent. What they still don't get is a
// font FAMILY, a text COLOUR, a highlight, an alignment or a line height —
// those are exactly what pasted Word content carries, and they're what makes
// one document stop looking like the same product as the next.
//
// The reconciliation is that the choices are a fixed scale rather than a
// number: the author picks "large", the stylesheet decides what large means,
// and restyling the whole document set stays a one-line change.
// ---------------------------------------------------------------------------
export const TEXT_SIZES = [
  { key: 'sm',   label: 'Small' },
  { key: 'base', label: 'Normal' },
  { key: 'lg',   label: 'Large' },
];
export const isTextSize = (k) => TEXT_SIZES.some(s => s.key === k);

// Header weights — Rob asked specifically for Metropolis Extra Light vs Semi
// Bold on headings. 'semibold' is the default because it's what every already
// published document renders as; anything else would restyle them retroactively.
export const HEADING_WEIGHTS = [
  { key: 'light',    label: 'Extra light' },
  { key: 'semibold', label: 'Semi bold' },
  { key: 'bold',     label: 'Bold' },
];
export const isHeadingWeight = (k) => HEADING_WEIGHTS.some(w => w.key === k);

export const MAX_INDENT = 3;
export const clampIndent = (n) => Math.max(0, Math.min(MAX_INDENT, Math.trunc(Number(n) || 0)));

// Properties that must be STRIPPED from pasted content. Kept as one list so the
// editor's paste handler and any future importer can't drift apart.
export const STRIPPED_ON_PASTE = ['font-family', 'font-size', 'color', 'background', 'background-color', 'text-align', 'line-height'];

export function newBlock(type) {
  const id = newBlockId();
  switch (type) {
    case 'heading':   return { id, type, text: '', level: 2, weight: 'semibold' };
    case 'paragraph': return { id, type, lines: [{ bullet: false, tokens: [{ t: 'text', v: '' }] }] };
    case 'bullet':    return { id, type, items: [''] };
    case 'clause':    return { id, type, summary: '', full: '' };
    case 'signature': return { id, type, statement: 'I confirm I have the authority to sign this document on behalf of my company.', extras: [] };
    // The structural unit of a long document. A break carries the title of the
    // section it OPENS — see lib/longDocument.
    case 'section_break': return { id, type, title: '' };
    case 'divider':   return { id, type };
    case 'table':     return { id, type, header: true, rows: [['', ''], ['', '']] };
    default:          return { id, type: 'paragraph', lines: [{ bullet: false, tokens: [{ t: 'text', v: '' }] }] };
  }
}

export function blankSchema() {
  return {
    editor: 'prose',
    blocks: [
      { ...newBlock('heading') },
      { ...newBlock('paragraph') },
    ],
  };
}

// Coerce a raw inline-token array into the canonical token model (text runs
// with optional bold/italic, identity field chips, supplier-fill input chips).
// Empty/absent → a single empty text run so a line is never token-less.
function coerceTokens(arr) {
  return Array.isArray(arr) && arr.length
    ? arr.map(t => {
        if (t?.t === 'field') return { t: 'field', source: t.source || FIELD_SOURCES[0].key };
        if (t?.t === 'input') return {
          t: 'input', id: t.id || newBlockId(),
          ftype: isFillType(t.ftype) ? t.ftype : 'short_text',
          label: String(t.label || fillFieldLabel(t.ftype)),
          optional: !!t.optional,
        };
        // An external link. `href` is kept only when it's a safe absolute URL —
        // a javascript: or data: href would otherwise end up rendered into the
        // stored signed document, which is served from storage.
        if (t?.t === 'link') return { t: 'link', v: String(t.v || ''), href: safeHref(t.href) };
        return {
          t: 'text', v: String(t?.v || ''),
          ...(t?.b ? { b: true } : {}), ...(t?.i ? { i: true } : {}), ...(t?.u ? { u: true } : {}),
          ...(isTextSize(t?.size) && t.size !== 'base' ? { size: t.size } : {}),
        };
      })
    : [{ t: 'text', v: '' }];
}

// Only http(s) and mailto survive. Anything else — javascript:, data:, vbscript:,
// or a protocol-relative //host — becomes no link at all rather than a link
// somewhere unexpected.
export function safeHref(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^(https?:\/\/|mailto:)/i.test(s)) return s;
  return '';
}

// Normalise anything loaded from the DB into a well-formed prose schema so the
// editor never has to defend against missing/partial shapes.
export function coerceSchema(raw) {
  if (!raw || raw.editor !== 'prose' || !Array.isArray(raw.blocks)) return blankSchema();
  const blocks = raw.blocks
    .filter(b => b && typeof b === 'object' && b.type)
    .map(b => {
      const id = b.id || newBlockId();
      if (b.type === 'heading') return {
        id, type: 'heading', text: String(b.text || ''),
        // Existing documents have neither — they are all level 2, semibold,
        // which is exactly what they render as today.
        level: b.level === 3 ? 3 : 2,
        weight: isHeadingWeight(b.weight) ? b.weight : 'semibold',
      };
      if (b.type === 'section_break') return { id, type: 'section_break', title: String(b.title || '') };
      if (b.type === 'divider') return { id, type: 'divider' };
      if (b.type === 'table') {
        // Rectangular by construction: every row padded to the widest, so the
        // renderer never has to defend against a ragged grid.
        const raw = Array.isArray(b.rows) && b.rows.length ? b.rows : [['', '']];
        const rows = raw.map(r => (Array.isArray(r) ? r.map(c => String(c == null ? '' : c)) : ['']));
        const width = Math.max(1, ...rows.map(r => r.length));
        return { id, type: 'table', header: b.header !== false, rows: rows.map(r => [...r, ...Array(width - r.length).fill('')]) };
      }
      if (b.type === 'clause') return { id, type: 'clause', summary: String(b.summary || ''), full: String(b.full || '') };
      if (b.type === 'signature') return {
        id, type: 'signature', statement: String(b.statement || ''),
        // Extra signature rows (Name & Job Title, Company, Address…). Each is
        // either prefilled from an identity source or supplier-filled (source
        // null). Keyed by their own id when the supplier fills them.
        extras: Array.isArray(b.extras)
          ? b.extras.map(e => ({
              id: e.id || newBlockId(),
              label: String(e.label || ''),
              source: (e.source && FIELD_SOURCES.some(s => s.key === e.source)) ? e.source : null,
            }))
          : [],
      };
      if (b.type === 'bullet') {
        const items = Array.isArray(b.items) && b.items.length ? b.items.map(i => String(i || '')) : [''];
        return { id, type: 'bullet', items };
      }
      // paragraph (default). Content is an ordered list of LINES; each line is
      // an array of inline tokens and can be a bullet. Back-compat: the old
      // shape stored a flat `tokens` array (one non-bullet line).
      // A line can be a bullet, a numbered item, or plain, and carries an
      // indent level for nesting. `numbered` and `bullet` are mutually
      // exclusive — a line is one kind of list item or none.
      let lines;
      const coerceLine = (ln) => {
        const numbered = !!ln?.numbered && !ln?.bullet;
        return {
          bullet: !!ln?.bullet,
          ...(numbered ? { numbered: true } : {}),
          ...(clampIndent(ln?.indent) ? { indent: clampIndent(ln.indent) } : {}),
          tokens: coerceTokens(ln?.tokens),
        };
      };
      if (Array.isArray(b.lines) && b.lines.length) {
        lines = b.lines.map(coerceLine);
      } else {
        lines = [{ bullet: false, tokens: coerceTokens(b.tokens) }];
      }
      return { id, type: 'paragraph', lines };
    });
  return { editor: 'prose', blocks: blocks.length ? blocks : blankSchema().blocks };
}

export const wordCount = (s) => (String(s || '').trim().match(/\S+/g) || []).length;

// The value a supplier confirmed for an identity field (falls back to the
// field's label as a placeholder if somehow unfilled).
export const fieldValueOf = (source, fields) => {
  const v = fields && fields[source];
  return v != null && String(v).trim() ? String(v) : `[${fieldLabel(source)}]`;
};

// One line's tokens flattened to plain text with field/input values filled in.
export const lineText = (line, fields) =>
  (line.tokens || []).map(t =>
    t.t === 'field' ? fieldValueOf(t.source, fields)
      : t.t === 'input' ? inputValueOf(t, fields)
        : t.v).join('');

// A paragraph flattened to plain text (lines joined by newlines), field/input
// values substituted in — used by the fill preview and the stored HTML copy.
export const paragraphText = (block, fields) =>
  (block.lines || []).map(l => lineText(l, fields)).join('\n');

// The Fusion marks, inlined so the stored/standalone signed document renders
// them with no external request (it's opened later from storage, offline).
// FUSION_MARK = the F glyph only (document header); FUSION_LOCKUP = the full
// FUSION + F landscape lockup (cover page). Fills are inline (no <style>) so
// nothing leaks into the surrounding document.
export const FUSION_MARK = `<svg viewBox="204 117 220 192" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fusion">`
  + `<polygon fill="#92D400" points="366.7,122.5 358,137.6 270.7,137.6 227.1,213.1 279.5,303.8 262,303.8 209.7,213.1 262,122.5 "/>`
  + `<polygon fill="#92D400" points="271.9,239 309.3,303.8 291.8,303.8 245.7,223.9 314.4,223.9 323.1,239 "/>`
  + `<polygon fill="#92D400" points="285.7,163.4 265.7,198 323.1,198 314.4,213.1 239.5,213.1 276.9,148.3 351.8,148.3 343.1,163.4 "/>`
  + `<polygon fill="#51534A" points="419,213.1 372.9,293.1 364.1,277.9 401.6,213.1 364.2,148.2 372.9,133.2 "/>`
  + `<polygon fill="#51534A" points="389.2,213.1 358,267.3 349.3,252.1 371.8,213.1 349.2,174.1 358,159 "/></svg>`;
export const FUSION_LOCKUP = `<svg viewBox="0 0 637.6 292.3" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fusion">`
  + `<polygon fill="#131313" points="536.6,123.3 536.6,152.8 514.6,123.3 504.9,123.3 504.9,168.9 514.6,168.9 514.6,139.5 536.6,168.9 546.4,168.9 546.4,123.3 "/>`
  + `<path fill="#131313" d="M481.2,127.3l-4.7,8.2c2.7,2.7,4.5,6.5,4.5,10.5c0,8-6.5,14.7-14.2,14.7c-1.5,0-3-0.3-4.4-0.8l-4.6,7.9c2.8,1.1,5.8,1.8,9,1.8c13.2,0,24.2-10.8,24.2-23.6C490.9,138.5,487,131.7,481.2,127.3 M466.7,131.4c1.5,0,3,0.3,4.4,0.8l4.6-7.9c-2.8-1.1-5.8-1.7-9-1.7c-13.1,0-24.2,10.8-24.2,23.6c0,7.6,3.9,14.4,9.7,18.8l4.7-8.2c-2.7-2.7-4.4-6.5-4.4-10.5C452.5,138.2,459,131.4,466.7,131.4"/>`
  + `<rect x="418.7" y="123.3" fill="#131313" width="9.8" height="45.6"/>`
  + `<path fill="#131313" d="M394,143.3c-1.7-0.6-3.6-1.1-5.7-1.5c-7-1.6-9.8-2.7-9.8-6c0-2.7,2.5-4.9,6.7-4.9c4.4,0,9,1.8,13,5l4.4-7.6c-4.5-3.6-10.1-5.7-17.1-5.7c-10,0-16.9,5.8-16.9,14.2c0,8.8,6.8,11.5,16.5,13.7c1.6,0.4,3,0.7,4.1,1c3.9,1.1,5.5,2.3,5.5,4.8c0,2.9-2.5,5.1-7.4,5.1c-5.8,0-10.7-2.3-15-6l-4.5,7.7c5.2,4.3,11.7,6.5,19.1,6.5c11.1,0,17.7-5.8,17.7-14.5C404.7,148.6,400.6,145.4,394,143.3"/>`
  + `<path fill="#131313" d="M313.4,149.2v-25.9h9.8v25.5c0,7,4.5,12,10.9,12c6.3,0,10.8-5,10.8-12v-25.5h9.8v25.9c0,12-8.6,20.5-20.6,20.5C321.9,169.7,313.4,161.2,313.4,149.2"/>`
  + `<polygon fill="#131313" points="298.7,132.2 298.7,123.3 260.6,123.3 260.6,168.9 270.4,168.9 270.4,150.9 290.2,150.9 295.3,142.1 270.4,142.1 270.4,132.2 "/>`
  + `<polygon fill="#92D400" points="186.5,91.1 181.2,100.3 128.2,100.3 101.8,146.1 133.5,201.2 123,201.2 91.2,146.1 123,91.1 "/>`
  + `<polygon fill="#92D400" points="128.9,161.8 151.6,201.2 141,201.2 113,152.7 154.7,152.7 160,161.8 "/>`
  + `<polygon fill="#92D400" points="137.3,116 125.2,137 160,137 154.7,146.1 109.3,146.1 132,106.8 177.4,106.8 172.1,116 "/>`
  + `<polygon fill="#DADCCD" points="218.2,146.1 190.2,194.7 184.9,185.4 207.6,146.1 185,106.7 190.2,97.6 "/>`
  + `<polygon fill="#DADCCD" points="200.1,146.1 181.2,179 175.9,169.8 189.6,146.1 175.9,122.5 181.2,113.3 "/></svg>`;

// ---------------------------------------------------------------------------
// Render a COMPLETED + SIGNED form to a standalone HTML document, stored in
// supplier-uploads so it flows through the normal upload-review pipeline (the
// admin "View uploaded file" opens exactly what the supplier signed). Pure
// string building — no DOM. `fields` = { source: value }, `ticks` =
// { blockId: true }. `subtitle` = optional cover descriptor line.
// ---------------------------------------------------------------------------
export function renderSubmissionHtml(schema, { fields = {}, ticks = {}, signerName = '', signedAtLabel = '', docLabel = 'Document', version = null, subtitle = '', signatureImage = '' } = {}) {
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const body = coerceSchema(schema).blocks.map(b => {
    if (b.type === 'heading') {
      const tag = b.level === 3 ? 'h3' : 'h2';
      const w = b.weight && b.weight !== 'semibold' ? ` class="w-${esc(b.weight)}"` : '';
      return `<${tag}${w}>${esc(b.text)}</${tag}>`;
    }
    // A section break is a page break in the printed/PDF record — which is the
    // only place pages exist (§2). Its title becomes the section's heading.
    if (b.type === 'section_break') {
      return `<div class="secbrk">${b.title ? `<h2 class="sectitle">${esc(b.title)}</h2>` : ''}</div>`;
    }
    if (b.type === 'divider') return `<hr/>`;
    if (b.type === 'table') {
      const rows = b.rows || [];
      if (!rows.length) return '';
      const cell = (c, head) => `<${head ? 'th' : 'td'}>${esc(c)}</${head ? 'th' : 'td'}>`;
      const head = b.header ? `<thead><tr>${rows[0].map(c => cell(c, true)).join('')}</tr></thead>` : '';
      const bodyRows = (b.header ? rows.slice(1) : rows).map(r => `<tr>${r.map(c => cell(c, false)).join('')}</tr>`).join('');
      return `<table class="doctbl">${head}<tbody>${bodyRows}</tbody></table>`;
    }
    if (b.type === 'paragraph') {
      const lineHtml = (line) => (line.tokens || []).map(t => {
        // Filled fields carry an accessible label — "Company name: Acme" — not
        // just a tinted span (styling brief §8).
        if (t.t === 'field') { const v = fieldValueOf(t.source, fields); return `<span class="fld" aria-label="${esc(`${fieldLabel(t.source)}: ${v}`)}">${esc(v)}</span>`; }
        if (t.t === 'input') {
          // An attachment answer is a {url,name} object, not a string. Without
          // this it rendered as "[object Object]" in the stored copy — the one
          // artefact that has to survive as evidence of what was signed.
          if (t.ftype === 'attachment') {
            const a = fields && fields[t.id];
            if (a?.url) {
              return `<a class="ans" href="${esc(safeHref(a.url) || '')}" rel="noopener noreferrer" aria-label="${esc(`${t.label}: ${a.name || 'attachment'}`)}">${esc(a.name || 'Attachment')}</a>`;
            }
            return `<span class="ans" aria-label="${esc(`${t.label}: not attached`)}">[${esc(t.label || 'Attachment')}]</span>`;
          }
          const v = inputValueOf(t, fields);
          return `<span class="ans" aria-label="${esc(`${t.label}: ${v}`)}">${esc(v)}</span>`;
        }
        if (t.t === 'link') {
          const href = safeHref(t.href);
          // An unsafe or missing href degrades to plain text — never to a link
          // pointing somewhere the author didn't intend.
          return href ? `<a href="${esc(href)}" rel="noopener noreferrer">${esc(t.v)}</a>` : esc(t.v);
        }
        let run = esc(t.v);
        if (t.i) run = `<em>${run}</em>`;
        if (t.b) run = `<strong>${run}</strong>`;
        if (t.u) run = `<u>${run}</u>`;
        if (t.size && t.size !== 'base') run = `<span class="sz-${esc(t.size)}">${run}</span>`;
        return run;
      }).join('');
      // Each plain line is its own <p> (so paragraph breaks survive); runs of
      // list lines collapse into one <ul>/<ol>. A run breaks when the marker
      // kind OR the indent level changes, so nesting produces nested lists
      // rather than one flat list with padding.
      let out = '';
      let buf = [];
      let bufKind = null;   // 'bullet' | 'numbered'
      let bufIndent = 0;
      const flush = () => {
        if (!buf.length) return;
        const tag = bufKind === 'numbered' ? 'ol' : 'ul';
        const cls = bufIndent ? ` class="ind-${bufIndent}"` : '';
        out += `<${tag}${cls}>${buf.map(h => `<li>${h}</li>`).join('')}</${tag}>`;
        buf = [];
      };
      for (const line of (b.lines || [])) {
        const h = lineHtml(line);
        const kind = line.bullet ? 'bullet' : line.numbered ? 'numbered' : null;
        const indent = clampIndent(line.indent);
        if (kind) {
          if (kind !== bufKind || indent !== bufIndent) { flush(); bufKind = kind; bufIndent = indent; }
          if (h.trim()) buf.push(h);
        } else {
          flush(); bufKind = null; bufIndent = 0;
          if (h.trim()) out += indent ? `<p class="ind-${indent}">${h}</p>` : `<p>${h}</p>`;
        }
      }
      flush();
      return out;
    }
    if (b.type === 'bullet') {
      const lis = (b.items || []).filter(i => String(i || '').trim()).map(i => `<li>${esc(i)}</li>`).join('');
      return lis ? `<ul>${lis}</ul>` : '';
    }
    if (b.type === 'clause') {
      // The tick is evidence; give it a text equivalent, not a glyph alone (§8).
      const agreed = ticks[b.id];
      const mark = agreed ? `<span class="ctick">✓ Agreed</span>` : `<span class="ctick no">Not agreed</span>`;
      return `<div class="clause"><div class="csum">${mark}<span>${esc(b.summary)}</span></div><div class="cfull">${esc(b.full)}</div></div>`;
    }
    if (b.type === 'signature') {
      const extras = (b.extras || []).filter(e => String(e.label || '').trim());
      const extrasHtml = extras.length
        ? `<table class="sigextra">${extras.map(e => {
            const val = e.source
              ? fieldValueOf(e.source, fields)
              : (fields[e.id] != null && String(fields[e.id]).trim() ? String(fields[e.id]) : '');
            return `<tr><td class="sxlbl">${esc(e.label)}</td><td class="sxval">${esc(val)}</td></tr>`;
          }).join('')}</table>`
        : '';
      // The signature cell shows the drawn signature image when present (the
      // supplier signs with the mouse/finger), falling back to the typed name.
      // The printed name is kept on the "Signed by" label either way.
      const sigCell = signatureImage
        ? `<img class="sigimg" src="${signatureImage}" alt="Signature"/>`
        : esc(signerName);
      return `<div class="sig"><p class="stmt">${esc(b.statement)}</p>${extrasHtml}`
        + `<table class="sigrow"><tr>`
        + `<td><div class="sigval">${sigCell}</div><div class="siglbl">Signed by${signerName ? ` — ${esc(signerName)}` : ''}</div></td>`
        + `<td><div class="sigval">${esc(signedAtLabel)}</div><div class="siglbl">Date</div></td>`
        + `</tr></table></div>`;
    }
    return '';
  }).join('\n');

  const footer = `${esc(docLabel)}${version ? ` · v${esc(version)}` : ''} · Fusion 2K Ltd`;
  // ONE stylesheet for signing, review and the PDF snapshot — @media print is
  // pagination ONLY, never type/colour/layout, so the signed record can't
  // diverge from what the supplier saw (styling brief §6).
  const style = `:root{--doc-measure:660px;--doc-body:15.5px;--doc-leading:1.75;--doc-ink:#2A2E24;--doc-h1:#92D400;--doc-h2:#808080;--doc-rule:#D7E3C4;--brand-dk:#92D400;--ink-3:#51534A}`
    + `*{box-sizing:border-box}html,body{margin:0;background:#fff}`
    + `body{font-family:'Metropolis',-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:var(--doc-ink);font-size:var(--doc-body);line-height:var(--doc-leading)}`
    // Headers use Metropolis (inherited) — semibold for section headers, the
    // cover title stays heavier. Per-block Extra-Light/Semi-Bold font options
    // are a separate editor feature (queued).
    + `h1,h2,h3,.cover-title{font-family:'Metropolis',-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}`
    + `.page{max-width:var(--doc-measure);margin:0 auto;padding:0 48px 48px}`
    + `.dochdr{text-align:center;padding:26px 0 22px}`
    + `.mark{display:flex;align-items:center;gap:16px;justify-content:center;margin-bottom:16px}`
    + `.mark .rule{flex:1;height:1px;background:linear-gradient(to right,transparent,var(--doc-rule))}.mark .rule.r{background:linear-gradient(to left,transparent,var(--doc-rule))}`
    + `.mark .wm{display:inline-flex;align-items:center}.mark .wm svg{width:38px;height:auto;display:block}`
    // Cover page — full first page: big lockup, document title, prepared-by /
    // date, a decorative corner triangle + green accent (styling adapts the
    // Fusion cover template). Its own page in print.
    + `.cover{position:relative;min-height:100vh;padding:64px 56px 56px;display:flex;flex-direction:column;overflow:hidden;page-break-after:always;break-after:page}`
    + `.cover>*{position:relative;z-index:1}`
    + `.cover-logo{margin-top:56px}.cover-logo svg{width:300px;max-width:60%;height:auto;display:block}`
    + `.cover-title{font-size:52px;line-height:1.05;font-weight:800;color:#5b5d54;margin:64px 0 0;max-width:64%}`
    + `.cover-sub{font-size:16px;color:var(--ink-3);margin:16px 0 0;max-width:64%}`
    + `.cover-meta{margin-top:auto;font-size:14px;line-height:2.1;color:var(--ink-3)}`
    + `.cover-meta .k{color:var(--doc-ink)}.cover-meta .v{color:var(--brand-dk);font-weight:600}`
    + `.cover-accent{width:56px;height:4px;background:#92D400;margin-top:16px}`
    + `.cover-tri{position:absolute;right:0;bottom:0;width:38%;height:54%;background:#DDE3D0;opacity:.55;z-index:0;clip-path:polygon(100% 0,100% 100%,0 100%)}`
    + `h1{font-size:26px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:var(--doc-h1);margin:0 0 6px}`
    + `.completed{font-size:12px;color:var(--ink-3)}`
    + `h2{font-size:15px;font-weight:600;color:var(--doc-h2);margin:26px 0 8px}`
    + `h3{font-size:14px;font-weight:700;color:var(--doc-h1);margin:20px 0 6px}`
    // Author-chosen heading weights. Extra Light is a real Metropolis weight
    // (200); the family is still fixed — only the weight is the author's call.
    + `.w-light{font-weight:200}.w-bold{font-weight:700}`
    // Author-chosen size STEPS, resolved here. The author picks "large"; this
    // decides what large means, so restyling every document stays one edit.
    + `.sz-sm{font-size:.86em}.sz-lg{font-size:1.18em}`
    + `p{margin:0 0 15px;text-align:left}ul,ol{margin:0 0 15px;padding-left:22px}li{margin:0 0 7px}li::marker{color:var(--brand-dk)}`
    + `.ind-1{margin-left:26px}.ind-2{margin-left:52px}.ind-3{margin-left:78px}`
    + `a{color:#2F6E14;text-decoration:underline}`
    // A section break starts a new page in print/PDF — the only place pages
    // exist. On screen it's a titled band, not a rule.
    + `.secbrk{margin:34px 0 4px;page-break-before:always;break-before:page}`
    + `.secbrk:first-child{page-break-before:avoid;break-before:auto}`
    + `.sectitle{font-size:19px;font-weight:700;color:var(--doc-h1);text-transform:none;letter-spacing:0;margin:0 0 4px;padding-bottom:8px;border-bottom:2px solid var(--doc-rule)}`
    + `.doctbl{width:100%;border-collapse:collapse;margin:0 0 16px;font-size:14px}`
    + `.doctbl th,.doctbl td{border:1px solid var(--doc-rule);padding:8px 11px;text-align:left;vertical-align:top}`
    + `.doctbl th{background:#F4F7EC;font-weight:600;color:var(--doc-ink)}`
    + `strong{font-weight:700;color:#808080}em{font-style:italic}hr{border:0;border-top:1px solid var(--doc-rule);margin:20px 0}`
    + `.fld,.ans{background:#eef2f6;color:#2b4a57;border-radius:4px;padding:0 5px;font-weight:600}`
    + `.clause{border:1px solid #e6e9da;border-left:3px solid var(--brand-dk);background:#fafbf6;border-radius:6px;padding:12px 14px;margin:14px 0}`
    + `.csum{font-size:15px;font-weight:600;display:flex;gap:8px;align-items:baseline}.ctick{color:var(--brand-dk);font-weight:700;white-space:nowrap}.ctick.no{color:#B71F06}`
    + `.cfull{color:var(--ink-3);font-size:13px;margin-top:6px;padding-left:22px;white-space:pre-wrap}`
    + `.sig{margin-top:30px;border-top:1px dashed #cfd3c2;padding-top:18px}.stmt{font-style:italic;color:#3a3b33;margin:0 0 12px}`
    + `.sigextra{width:100%;margin:0 0 12px;border-collapse:collapse}.sigextra td{border:1px solid var(--doc-rule);padding:8px 12px;font-size:14px}.sxlbl{width:38%;color:var(--ink-3);font-weight:600}.sxval{color:var(--doc-ink)}`
    + `.sigrow{width:100%;margin-top:6px}.sigrow td{vertical-align:bottom;padding-right:28px}`
    + `.sigval{font-size:18px;border-bottom:2px solid rgba(19,19,19,.4);padding-bottom:2px;min-width:180px}`
    + `.sigimg{max-height:60px;max-width:260px;display:block}`
    + `.siglbl{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#9BA08C;margin-top:4px}`
    + `.docftr{max-width:var(--doc-measure);margin:36px auto 0;padding:12px 48px 0;border-top:1px solid var(--doc-rule);color:var(--ink-3);font-size:11px;text-align:center}`
    + `@media print{@page{margin:18mm 16mm}.clause,.sig,li,.doctbl tr{break-inside:avoid}.doctbl thead{display:table-header-group}h2,h3,.sectitle{break-after:avoid}.docftr{position:fixed;bottom:0;left:0;right:0;border:0;margin:0}.pageno::after{content:"Page " counter(page) " of " counter(pages)}}`;

  const cover = `<section class="cover">`
    + `<div class="cover-logo">${FUSION_LOCKUP}</div>`
    + `<div class="cover-title">${esc(docLabel)}</div>`
    + (subtitle ? `<div class="cover-sub">${esc(subtitle)}</div>` : '')
    + `<div class="cover-meta">`
    + (version ? `<div><span class="k">Version:</span> <span class="v">${esc(version)}</span></div>` : '')
    + (signedAtLabel ? `<div><span class="k">Date:</span> <span class="v">${esc(signedAtLabel)}</span></div>` : '')
    + `<div class="cover-accent"></div>`
    + `</div>`
    + `<div class="cover-tri" aria-hidden="true"></div>`
    + `</section>`;

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">`
    + `<title>${esc(docLabel)} — signed by ${esc(signerName)}</title><style>${style}</style></head><body>`
    + cover
    + `<div class="page"><div class="dochdr">`
    + `<div class="mark"><span class="rule"></span><span class="wm">${FUSION_MARK}</span><span class="rule r"></span></div>`
    + `<h1>${esc(docLabel)}</h1>`
    + `<div class="completed">Completed by ${esc(signerName)}${signedAtLabel ? ` on ${esc(signedAtLabel)}` : ''}${version ? ` · version ${esc(version)}` : ''}</div>`
    + `</div>${body}</div>`
    + `<div class="docftr">${footer} <span class="pageno"></span></div>`
    + `</body></html>`;
}

const blockPlainText = (b) => {
  if (b.type === 'heading') return b.text || '';
  if (b.type === 'paragraph') return (b.lines || []).map(l => (l.tokens || []).map(t => t.t === 'field' ? fieldLabel(t.source) : t.v).join('')).join(' ');
  if (b.type === 'bullet') return (b.items || []).join(' ');
  if (b.type === 'clause') return `${b.summary || ''} ${b.full || ''}`;
  if (b.type === 'signature') return b.statement || '';
  if (b.type === 'section_break') return b.title || '';
  if (b.type === 'table') return (b.rows || []).flat().join(' ');
  // A divider is structure, not content — a document of nothing but dividers
  // is still empty, so it must not satisfy isBuilt().
  return '';
};

// A form is "built" once it has any real authored content — used for the
// "Draft — not built" state and the Risk-Flag tick refusal.
export function isBuilt(schema) {
  const s = coerceSchema(schema);
  return s.blocks.some(b => blockPlainText(b).trim().length > 0);
}

// ---------------------------------------------------------------------------
// Validation — the blocking problems that keep a form from publishing. Each is
// { blockId|null, message }. An empty array means it's publishable.
// ---------------------------------------------------------------------------
export function computeProblems(schema) {
  const s = coerceSchema(schema);
  const problems = [];
  const sigIndexes = [];

  s.blocks.forEach((b, i) => {
    if (b.type === 'signature') sigIndexes.push(i);
    if (b.type === 'clause') {
      if (!String(b.summary || '').trim()) {
        problems.push({ blockId: b.id, message: 'A clause needs a short summary.' });
      } else if (wordCount(b.summary) > CLAUSE_SUMMARY_MAX_WORDS) {
        problems.push({ blockId: b.id, message: `A clause summary is over ${CLAUSE_SUMMARY_MAX_WORDS} words (currently ${wordCount(b.summary)}).` });
      }
      if (!String(b.full || '').trim()) {
        problems.push({ blockId: b.id, message: 'A clause needs its full wording.' });
      }
    }
    if (b.type === 'signature' && !String(b.statement || '').trim()) {
      problems.push({ blockId: b.id, message: 'The signature block needs an authority statement.' });
    }
    // A signature is SUBSTANCE — it must be typed, never prefilled (review
    // brief §7). A signature field bound to a prefill source blocks publish.
    if (b.type === 'signature' && (b.extras || []).some(e => e.source)) {
      problems.push({ blockId: b.id, message: 'A signature field can’t be prefilled — the signatory must type it. Remove the prefill source.' });
    }
    if (b.type === 'paragraph') {
      const allTokens = (b.lines || []).flatMap(l => l.tokens || []);
      for (const t of allTokens) {
        if (t.t === 'field' && !FIELD_SOURCES.some(fs => fs.key === t.source)) {
          problems.push({ blockId: b.id, message: 'A field has no valid source selected.' });
          break;
        }
        if (t.t === 'input' && !String(t.label || '').trim()) {
          problems.push({ blockId: b.id, message: 'A fill-in field has no label.' });
          break;
        }
      }
    }
  });

  // Signature: exactly one, and it must be the last block.
  if (sigIndexes.length === 0) {
    problems.push({ blockId: null, message: 'Add a signature block at the end.' });
  } else if (sigIndexes.length > 1) {
    problems.push({ blockId: null, message: 'Only one signature block is allowed.' });
  } else if (sigIndexes[0] !== s.blocks.length - 1) {
    problems.push({ blockId: null, message: 'The signature block must be the last block.' });
  }

  // Must have at least one clause or some body — an empty read-and-sign form
  // has nothing to agree to.
  const hasContent = s.blocks.some(b => (b.type === 'clause' || b.type === 'paragraph' || b.type === 'heading' || b.type === 'bullet') && blockPlainText(b).trim());
  if (!hasContent) {
    problems.push({ blockId: null, message: 'Add some content before publishing.' });
  }

  return problems;
}
